import React from "react";
import { Box, Typography } from "@mui/material";
import {
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  DAILY_METRICS_CHART_COLORS,
  type DailyMetricsChartPoint,
} from "./dailyMetricsChart";
import { MINIMAL_CHART_AXIS } from "./chartAxisProps";

function formatNumber(num: number): string {
  if (num >= 1_000_000) return `${(num / 1_000_000).toFixed(1)}M`;
  if (num >= 1_000) return `${(num / 1_000).toFixed(1)}K`;
  return num.toString();
}

type Props = {
  data: DailyMetricsChartPoint[];
  height?: number;
  showViews: boolean;
  showLikes: boolean;
  showComments: boolean;
};

function LineChartTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: Array<{ name?: string; value?: number; color?: string }>;
  label?: string;
}) {
  if (!active || !payload?.length) return null;

  return (
    <Box
      sx={{
        bgcolor: "background.paper",
        border: 1,
        borderColor: "divider",
        borderRadius: 1,
        px: 1.25,
        py: 0.75,
      }}
    >
      <Typography
        variant="caption"
        sx={{
          display: "block",
          fontWeight: 700,
          color: "text.primary",
          mb: 0.5,
        }}
      >
        {label}
      </Typography>
      {payload.map((row) => (
        <Typography
          key={String(row.name)}
          variant="caption"
          sx={{ display: "block", color: "text.primary", lineHeight: 1.45 }}
        >
          <Box
            component="span"
            sx={{
              display: "inline-block",
              width: 8,
              height: 8,
              borderRadius: "50%",
              bgcolor: row.color ?? "text.secondary",
              mr: 0.75,
              verticalAlign: "middle",
            }}
          />
          {row.name}: {formatNumber(Number(row.value))}
        </Typography>
      ))}
    </Box>
  );
}

export const DailyMetricsLineChart: React.FC<Props> = ({
  data,
  height = 280,
  showViews,
  showLikes,
  showComments,
}) => (
  <Box sx={{ width: "100%", height }}>
    <ResponsiveContainer>
      <LineChart
        data={data}
        margin={{ top: 8, right: 16, left: 4, bottom: 8 }}
      >
        <XAxis dataKey="label" {...MINIMAL_CHART_AXIS} />
        <YAxis
          {...MINIMAL_CHART_AXIS}
          tickFormatter={(v) => formatNumber(Number(v))}
        />
        <RechartsTooltip content={<LineChartTooltip />} />
        <Legend />
        {showViews && (
          <Line
            type="monotone"
            dataKey="Views"
            stroke={DAILY_METRICS_CHART_COLORS.views}
            strokeWidth={2}
            dot={false}
            activeDot={false}
          />
        )}
        {showLikes && (
          <Line
            type="monotone"
            dataKey="Likes"
            stroke={DAILY_METRICS_CHART_COLORS.likes}
            strokeWidth={2}
            dot={false}
            activeDot={false}
          />
        )}
        {showComments && (
          <Line
            type="monotone"
            dataKey="Comments"
            stroke={DAILY_METRICS_CHART_COLORS.comments}
            strokeWidth={2}
            dot={false}
            activeDot={false}
          />
        )}
      </LineChart>
    </ResponsiveContainer>
  </Box>
);
